import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Textarea } from "../components/ui/textarea";
import { Input } from "../components/ui/input";
import { Star, ArrowLeft, Send } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AllReviews = () => {
  const navigate = useNavigate();
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [newReview, setNewReview] = useState({
    name: "",
    country: "",
    text: ""
  });

  const fetchReviews = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/reviews`);
      setReviews(response.data);
    } catch (error) {
      console.error("Error fetching reviews:", error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchReviews();
  }, [fetchReviews]);

  const handleSubmitReview = async (e) => {
    e.preventDefault();
    
    if (!newReview.name || !newReview.text) {
      toast.error("Please fill in your name and review text");
      return;
    }

    setSubmitting(true);
    try {
      await axios.post(`${API}/reviews`, newReview);
      toast.success("Thank you! Your review has been submitted and is pending approval.");
      setNewReview({ name: "", country: "", text: "" });
    } catch (error) {
      console.error("Error submitting review:", error);
      toast.error("Failed to submit review. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#04539F] via-[#0668C7] to-[#0881F0]">
      {/* Header */}
      <div className="bg-white/10 backdrop-blur-md border-b border-white/20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex justify-between items-start">
            <div>
              <Button
                variant="ghost"
                className="text-white hover:text-white/80 mb-4"
                onClick={() => navigate("/")}
                data-testid="back-btn"
              >
                <ArrowLeft className="w-5 h-5 mr-2" />
                Back to Home
              </Button>
              <h1 className="text-4xl font-bold text-white mb-2" data-testid="reviews-title">
                Student Reviews
              </h1>
              <p className="text-white/90">{reviews.length} reviews from students worldwide</p>
            </div>
            <img 
              src="https://customer-assets.emergentagent.com/job_languagecoach/artifacts/iatwncw6_Logo%20le%20prof.png" 
              alt="Le Prof Berni Logo" 
              className="w-16 h-16 rounded-full object-cover border-2 border-white shadow-lg"
              data-testid="reviews-logo"
            />
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Submit Review Section */}
        <Card className="mb-12 shadow-xl" data-testid="submit-review-card">
          <CardContent className="p-8">
            <h2 className="text-2xl font-bold text-[#04539F] mb-4">Share Your Experience</h2>
            <p className="text-gray-600 mb-6">
              Have you taken classes with LeProfBerni? We'd love to hear about your experience!
            </p>
            <form onSubmit={handleSubmitReview} className="space-y-4">
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Your Name *
                  </label>
                  <Input
                    value={newReview.name}
                    onChange={(e) => setNewReview({ ...newReview, name: e.target.value })}
                    placeholder="Enter your name"
                    data-testid="review-name-input"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Country (Optional)
                  </label>
                  <Input
                    value={newReview.country}
                    onChange={(e) => setNewReview({ ...newReview, country: e.target.value })}
                    placeholder="Enter your country"
                    data-testid="review-country-input"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Your Review *
                </label>
                <Textarea
                  value={newReview.text}
                  onChange={(e) => setNewReview({ ...newReview, text: e.target.value })}
                  placeholder="Share your experience learning with LeProfBerni..."
                  rows={5}
                  data-testid="review-text-input"
                />
              </div>
              <Button
                type="submit"
                disabled={submitting}
                className="bg-[#04539F] hover:bg-[#033b7a] text-white"
                data-testid="submit-review-btn"
              >
                <Send className="w-4 h-4 mr-2" />
                {submitting ? "Submitting..." : "Submit Review"}
              </Button>
              <p className="text-sm text-gray-500 mt-2">
                * Your review will be published after approval
              </p>
            </form>
          </CardContent>
        </Card>

        {/* All Reviews */}
        <h2 className="text-3xl font-bold text-white mb-8" data-testid="all-reviews-title">
          All Reviews
        </h2>
        {loading ? (
          <div className="text-center text-white py-12">
            <p>Loading reviews...</p>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {reviews.map((review, idx) => (
              <Card key={review.id || `${review.name}-${idx}`} className="shadow-lg hover:shadow-xl transition-shadow" data-testid={`review-card-${idx}`}>
                <CardContent className="p-6 space-y-4">
                  <div className="flex text-[#04539F]">
                    {[...Array(5)].map((_, i) => (
                      <Star key={`star-${i}`} className={`w-4 h-4 ${i < (review.rating || 5) ? 'fill-current' : 'text-gray-300'}`} />
                    ))}
                  </div>
                  <p className="text-gray-700 italic whitespace-pre-line">"{review.text}"</p>
                  <div>
                    <p className="font-semibold text-[#04539F]">{review.name}</p>
                    {review.country && (
                      <p className="text-sm text-gray-500">{review.country}</p>
                    )}
                    {review.timestamp && (
                      <p className="text-xs text-gray-400 mt-1" data-testid={`review-date-${idx}`}>
                        {new Date(review.timestamp).toLocaleDateString('en-US', { year: 'numeric', month: 'long' })}
                      </p>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* Footer */}
      <footer className="bg-[#04539F] text-white py-8 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto text-center">
          <a
            href="https://app.emergent.sh/?utm_source=emergent-badge"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 text-white/60 hover:text-white/80 text-xs transition-colors"
          >
            <img
              src="https://avatars.githubusercontent.com/in/1201222?s=120&u=2686cf91179bbafbc7a71bfbc43004cf9ae1acea&v=4"
              alt="Emergent"
              className="w-4 h-4"
            />
            Made with Emergent
          </a>
        </div>
      </footer>
    </div>
  );
};

export default AllReviews;
